import { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { RetrovisorViatura, RodaViatura } from './viaturas/pecasViatura'

/**
 * Objetos da mesa no acabamento de maquete: caminhão de carga, moto, árvore,
 * defensa metálica e muro de concreto. Mesma família das viaturas (roda com
 * porca, vidro, faixa) e das árvores do tabuleiro.
 */

const semRaycast = () => null
const PRETO = '#15181c'
const CROMO = '#d9dee4'
const VIDRO = '#5a7890'
const vidro = <meshStandardMaterial color={VIDRO} metalness={0.3} roughness={0.1} envMapIntensity={1.4} />

/** Caminhão de carga com baú, ~7,5 m, frente em −X. */
export function CaminhaoCarga3D({ cor }: { cor: string }) {
  const X_FRENTE = -3.7
  const X_CABINE = -1.7
  const X_TRASEIRA = 3.8
  const MEIA = 1.15
  const xCab = (X_FRENTE + X_CABINE) / 2
  const xBau = (X_CABINE + 0.15 + X_TRASEIRA) / 2
  const cBau = X_TRASEIRA - X_CABINE - 0.15
  return (
    <group name="caminhao-carga">
      <mesh raycast={semRaycast} castShadow position={[0, 0.78, 0]}>
        <boxGeometry args={[X_TRASEIRA - X_FRENTE - 0.3, 0.22, 0.95]} />
        <meshStandardMaterial color={PRETO} roughness={0.7} />
      </mesh>
      {/* cabine */}
      <mesh raycast={semRaycast} castShadow receiveShadow position={[xCab, 1.95, 0]}>
        <CaixaArredondada args={[X_CABINE - X_FRENTE, 1.95, MEIA * 2]} raio={0.14} />
        <meshStandardMaterial color={cor} roughness={0.32} metalness={0.15} />
      </mesh>
      <mesh raycast={semRaycast} position={[X_FRENTE - 0.005, 2.35, 0]} rotation={[0, 0, -0.08]}>
        <boxGeometry args={[0.02, 0.8, MEIA * 2 - 0.2]} />
        {vidro}
      </mesh>
      <mesh raycast={semRaycast} position={[X_FRENTE - 0.01, 1.45, 0]}>
        <boxGeometry args={[0.02, 0.6, 1.4]} />
        <meshStandardMaterial color="#1b1f24" roughness={0.6} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} raycast={semRaycast} position={[X_FRENTE - 0.025, 1.24 + i * 0.14, 0]}>
          <boxGeometry args={[0.02, 0.035, 1.36]} />
          <meshStandardMaterial color={CROMO} metalness={0.85} roughness={0.2} />
        </mesh>
      ))}
      {[-1, 1].map((lado) => (
        <group key={lado}>
          <mesh raycast={semRaycast} position={[X_FRENTE - 0.02, 1.4, lado * 0.92]}>
            <boxGeometry args={[0.03, 0.24, 0.34]} />
            <meshStandardMaterial color="#fffbe8" emissive="#fff6d6" emissiveIntensity={0.8} />
          </mesh>
          <mesh raycast={semRaycast} position={[X_FRENTE + 0.95, 2.3, lado * (MEIA + 0.004)]}>
            <boxGeometry args={[1.1, 0.68, 0.012]} />
            {vidro}
          </mesh>
          <mesh raycast={semRaycast} position={[X_FRENTE + 1.55, 1.95, lado * (MEIA + 0.004)]}>
            <boxGeometry args={[0.015, 1.8, 0.012]} />
            <meshStandardMaterial color="#7a1f1f" roughness={0.5} />
          </mesh>
          <mesh raycast={semRaycast} position={[X_FRENTE + 1.25, 1.85, lado * (MEIA + 0.016)]}>
            <boxGeometry args={[0.16, 0.035, 0.02]} />
            <meshStandardMaterial color={CROMO} metalness={0.85} roughness={0.2} />
          </mesh>
          <mesh raycast={semRaycast} castShadow position={[X_FRENTE + 0.95, 0.72, lado * (MEIA - 0.05)]}>
            <boxGeometry args={[0.55, 0.04, 0.24]} />
            <meshStandardMaterial color="#9aa3ad" metalness={0.7} roughness={0.4} />
          </mesh>
          <RetrovisorViatura posicao={[X_FRENTE + 0.25, 2.3, lado * MEIA]} lado={lado as 1 | -1} />
          {/* protetor lateral entre os eixos */}
          <mesh raycast={semRaycast} position={[0.3, 0.72, lado * (MEIA - 0.05)]}>
            <boxGeometry args={[2.6, 0.12, 0.04]} />
            <meshStandardMaterial color="#9aa3ad" metalness={0.6} roughness={0.45} />
          </mesh>
          {/* nervuras verticais do baú */}
          {Array.from({ length: 11 }, (_, i) => (
            <mesh key={i} raycast={semRaycast} position={[X_CABINE + 0.3 + i * ((cBau - 0.3) / 10), 2.15, lado * (MEIA + 0.012)]}>
              <boxGeometry args={[0.05, 2.2, 0.02]} />
              <meshStandardMaterial color="#b9c2cb" metalness={0.6} roughness={0.35} />
            </mesh>
          ))}
          <mesh raycast={semRaycast} position={[(X_FRENTE + X_TRASEIRA) / 2, 1.0, lado * (MEIA + 0.02)]}>
            <boxGeometry args={[X_TRASEIRA - X_FRENTE - 0.3, 0.06, 0.006]} />
            <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.2} />
          </mesh>
        </group>
      ))}
      <mesh raycast={semRaycast} castShadow position={[X_FRENTE - 0.1, 0.92, 0]}>
        <CaixaArredondada args={[0.24, 0.3, MEIA * 2 + 0.05]} raio={0.04} />
        <meshStandardMaterial color="#2a2f35" metalness={0.5} roughness={0.45} />
      </mesh>
      {/* baú de alumínio */}
      <mesh raycast={semRaycast} castShadow receiveShadow position={[xBau, 2.15, 0]}>
        <CaixaArredondada args={[cBau, 2.4, MEIA * 2 + 0.02]} raio={0.05} />
        <meshStandardMaterial color="#dfe4e9" metalness={0.35} roughness={0.45} />
      </mesh>
      {/* portas traseiras com barras de travamento */}
      <group position={[X_TRASEIRA + 0.01, 2.15, 0]}>
        <mesh raycast={semRaycast}>
          <boxGeometry args={[0.012, 2.2, 0.02]} />
          <meshStandardMaterial color="#6b7480" />
        </mesh>
        {[-0.75, -0.35, 0.35, 0.75].map((z) => (
          <mesh key={z} raycast={semRaycast} position={[0.02, 0, z]}>
            <cylinderGeometry args={[0.02, 0.02, 2.1, 8]} />
            <meshStandardMaterial color={CROMO} metalness={0.85} roughness={0.25} />
          </mesh>
        ))}
        {[-1, 1].map((lado) => (
          <mesh key={lado} raycast={semRaycast} position={[0.005, -1.2, lado * 0.9]}>
            <boxGeometry args={[0.02, 0.2, 0.22]} />
            <meshStandardMaterial color="#b91c1c" emissive="#ef4444" emissiveIntensity={0.6} />
          </mesh>
        ))}
      </group>
      {[-1, 1].map((lado) => (
        <group key={lado}>
          <RodaViatura posicao={[-2.6, 0.5, lado * (MEIA - 0.17)]} lado={lado as 1 | -1} />
          <RodaViatura posicao={[1.9, 0.5, lado * (MEIA - 0.17)]} lado={lado as 1 | -1} dupla />
          <RodaViatura posicao={[3.0, 0.5, lado * (MEIA - 0.17)]} lado={lado as 1 | -1} dupla />
          {[-2.6, 2.45].map((x) => (
            <mesh key={x} raycast={semRaycast} castShadow position={[x, 1.08, lado * (MEIA - 0.02)]}>
              <CaixaArredondada args={[x > 0 ? 2.3 : 1.2, 0.1, 0.42]} raio={0.04} />
              <meshStandardMaterial color={PRETO} roughness={0.75} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  )
}

/** Pneu de moto em pé, com aro e raios, no plano XY. */
function RodaMoto({ x }: { x: number }) {
  const r = 0.31
  return (
    <group position={[x, r, 0]}>
      <mesh raycast={semRaycast} castShadow>
        <torusGeometry args={[r - 0.055, 0.06, 12, 28]} />
        <meshStandardMaterial color="#141619" roughness={0.9} />
      </mesh>
      <mesh raycast={semRaycast}>
        <torusGeometry args={[r - 0.12, 0.014, 6, 28]} />
        <meshStandardMaterial color="#c7ccd2" metalness={0.8} roughness={0.3} />
      </mesh>
      {Array.from({ length: 6 }, (_, i) => (
        <mesh key={i} raycast={semRaycast} rotation={[0, 0, (i / 6) * Math.PI]}>
          <boxGeometry args={[(r - 0.12) * 2, 0.018, 0.012]} />
          <meshStandardMaterial color="#c7ccd2" metalness={0.8} roughness={0.3} />
        </mesh>
      ))}
      <mesh raycast={semRaycast} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 0.12, 12]} />
        <meshStandardMaterial color="#8d949c" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* disco de freio */}
      <mesh raycast={semRaycast} position={[0, 0, 0.04]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.006, 20]} />
        <meshStandardMaterial color="#9aa3ad" metalness={0.85} roughness={0.25} />
      </mesh>
    </group>
  )
}

/** Moto de rua em pé no descanso lateral, ~2 m, frente em +X. */
export function Moto3D({ cor }: { cor: string }) {
  return (
    // inclinada no descanso lateral, para o lado −Z
    <group name="moto" rotation={[0.1, 0, 0]}>
      <RodaMoto x={-0.68} />
      <RodaMoto x={0.7} />
      {/* garfo dianteiro e guidão */}
      {[-0.06, 0.06].map((z) => (
        <mesh key={z} raycast={semRaycast} castShadow position={[0.6, 0.62, z]} rotation={[0, 0, 0.42]}>
          <cylinderGeometry args={[0.02, 0.022, 0.72, 8]} />
          <meshStandardMaterial color={CROMO} metalness={0.85} roughness={0.2} />
        </mesh>
      ))}
      <mesh raycast={semRaycast} castShadow position={[0.47, 0.98, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.014, 0.014, 0.66, 8]} />
        <meshStandardMaterial color="#2a2f35" metalness={0.6} roughness={0.4} />
      </mesh>
      {[-0.33, 0.33].map((z) => (
        <mesh key={z} raycast={semRaycast} position={[0.47, 0.98, z]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.022, 0.022, 0.1, 8]} />
          <meshStandardMaterial color={PRETO} roughness={0.8} />
        </mesh>
      ))}
      {/* farol e para-lama */}
      <mesh raycast={semRaycast} position={[0.62, 0.88, 0]}>
        <CaixaArredondada args={[0.12, 0.16, 0.18]} raio={0.04} />
        <meshStandardMaterial color={cor} roughness={0.35} />
      </mesh>
      <mesh raycast={semRaycast} position={[0.685, 0.88, 0]}>
        <boxGeometry args={[0.01, 0.1, 0.12]} />
        <meshStandardMaterial color="#fffbe8" emissive="#fff6d6" emissiveIntensity={0.8} />
      </mesh>
      <mesh raycast={semRaycast} position={[0.7, 0.66, 0]} rotation={[0, 0, -0.2]}>
        <CaixaArredondada args={[0.32, 0.03, 0.12]} raio={0.012} />
        <meshStandardMaterial color={cor} roughness={0.35} />
      </mesh>
      {/* quadro, tanque, banco e rabeta */}
      <mesh raycast={semRaycast} castShadow position={[0.0, 0.52, 0]} rotation={[0, 0, -0.35]}>
        <cylinderGeometry args={[0.025, 0.025, 0.8, 8]} />
        <meshStandardMaterial color="#2a2f35" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[0.22, 0.86, 0]} scale={[1.4, 0.7, 0.85]}>
        <sphereGeometry args={[0.18, 18, 12]} />
        <meshStandardMaterial color={cor} roughness={0.25} metalness={0.2} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[-0.22, 0.82, 0]}>
        <CaixaArredondada args={[0.55, 0.08, 0.24]} raio={0.035} />
        <meshStandardMaterial color="#1b1e22" roughness={0.85} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[-0.56, 0.78, 0]} rotation={[0, 0, 0.18]}>
        <CaixaArredondada args={[0.36, 0.08, 0.18]} raio={0.03} />
        <meshStandardMaterial color={cor} roughness={0.3} />
      </mesh>
      <mesh raycast={semRaycast} position={[-0.76, 0.76, 0]}>
        <boxGeometry args={[0.02, 0.06, 0.12]} />
        <meshStandardMaterial color="#b91c1c" emissive="#ef4444" emissiveIntensity={0.6} />
      </mesh>
      {/* motor e escapamento */}
      <mesh raycast={semRaycast} castShadow position={[0.05, 0.45, 0]}>
        <CaixaArredondada args={[0.36, 0.3, 0.26]} raio={0.06} />
        <meshStandardMaterial color="#3a3f46" metalness={0.5} roughness={0.45} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[-0.38, 0.36, 0.17]} rotation={[0, 0, Math.PI / 2 - 0.15]}>
        <cylinderGeometry args={[0.045, 0.04, 0.62, 12]} />
        <meshStandardMaterial color={CROMO} metalness={0.9} roughness={0.2} />
      </mesh>
      {/* balança traseira */}
      <mesh raycast={semRaycast} position={[-0.38, 0.36, -0.08]} rotation={[0, 0, 0.12]}>
        <boxGeometry args={[0.62, 0.05, 0.03]} />
        <meshStandardMaterial color="#2a2f35" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* descanso lateral apoiado no chão */}
      <mesh raycast={semRaycast} position={[-0.05, 0.2, -0.16]} rotation={[0.55, 0, 0.2]}>
        <cylinderGeometry args={[0.012, 0.012, 0.42, 6]} />
        <meshStandardMaterial color="#2a2f35" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  )
}

/** Árvore no mesmo estilo das árvores do tabuleiro: tronco e copa facetada. */
export function ArvoreMaquete({ altura = 2.3, raio = 1.25, cor = '#46783a' }: { altura?: number; raio?: number; cor?: string }) {
  return (
    <group name="arvore">
      <mesh raycast={semRaycast} castShadow position={[0, altura / 2, 0]}>
        <cylinderGeometry args={[0.13, 0.2, altura, 8]} />
        <meshStandardMaterial color="#5a4030" roughness={0.95} />
      </mesh>
      <mesh castShadow position={[0, altura + raio * 0.7, 0]} scale={[1, 1.15, 1]}>
        <icosahedronGeometry args={[raio, 1]} />
        <meshStandardMaterial color={cor} roughness={0.9} flatShading />
      </mesh>
      <mesh castShadow position={[raio * 0.4, altura + raio * 1.3, -raio * 0.2]}>
        <icosahedronGeometry args={[raio * 0.62, 1]} />
        <meshStandardMaterial color="#3b6b32" roughness={0.9} flatShading />
      </mesh>
      <mesh castShadow position={[-raio * 0.45, altura + raio * 0.55, raio * 0.25]}>
        <icosahedronGeometry args={[raio * 0.55, 1]} />
        <meshStandardMaterial color="#557f3f" roughness={0.9} flatShading />
      </mesh>
    </group>
  )
}

/** Perfil "W" da lâmina de defensa metálica, em metros, no plano YZ. */
function perfilW(): THREE.Shape {
  const f = new THREE.Shape()
  const a = 0.31 / 2
  const pontos: Array<[number, number]> = [
    [-a, 0], [-a * 0.55, 0.075], [0, 0.02], [a * 0.55, 0.075], [a, 0],
    [a, 0.012], [a * 0.55, 0.087], [0, 0.032], [-a * 0.55, 0.087], [-a, 0.012],
  ]
  f.moveTo(pontos[0]![1], pontos[0]![0])
  for (const [y, z] of pontos.slice(1)) f.lineTo(z, y)
  f.closePath()
  return f
}

/** Defensa metálica: lâmina em W galvanizada sobre postes, com refletores. */
export function DefensaMaquete() {
  const comprimento = 4.2
  const geometria = useMemo(() => {
    const g = new THREE.ExtrudeGeometry(perfilW(), { depth: comprimento, bevelEnabled: false })
    // a extrusão corre em Z; deita ao longo de X
    g.rotateY(Math.PI / 2)
    g.translate(-comprimento / 2, 0, 0)
    g.computeVertexNormals()
    return g
  }, [])
  useEffect(() => () => geometria.dispose(), [geometria])
  return (
    <group name="defensa">
      <mesh raycast={semRaycast} castShadow geometry={geometria} position={[0, 0.62, -0.09]}>
        <meshStandardMaterial color="#b4bec6" metalness={0.75} roughness={0.38} side={2} />
      </mesh>
      {[-1.9, -0.95, 0, 0.95, 1.9].map((x) => (
        <group key={x} position={[x, 0, 0.08]}>
          <mesh raycast={semRaycast} castShadow position={[0, 0.38, 0]}>
            <boxGeometry args={[0.1, 0.76, 0.15]} />
            <meshStandardMaterial color="#8a949c" metalness={0.65} roughness={0.45} />
          </mesh>
          <mesh raycast={semRaycast} position={[0, 0.6, -0.12]}>
            <boxGeometry args={[0.06, 0.12, 0.03]} />
            <meshStandardMaterial color="#f8fafc" metalness={0.4} roughness={0.4} />
          </mesh>
          <mesh raycast={semRaycast} position={[0, 0.6, -0.136]}>
            <boxGeometry args={[0.04, 0.07, 0.004]} />
            <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.4} />
          </mesh>
        </group>
      ))}
    </group>
  )
}
